import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, Paper, Button, CircularProgress, Divider, ListItemButton } from '@/ui';
import { CheckCircleIcon, AddIcon } from '@/ui';
import Header from '@/components/Header';
import BottomTabBar from '@/components/BottomTabBar';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { httpClient } from '@/shared/api/http';
import { getCustomerId } from '@/shared/lib/utils/getCustomerId';
import { buildOpenBankingFetchPayload } from '@/shared/lib/openBankingFetchPayload';
import { useAuth } from '@/shared/state/store';
import { parseUnknownError } from '@/shared/lib/errorHandling';
import { OnboardingService } from '@/shared/api/services';

interface BankData {
  providerLogoUri: string;
  providerDisplayName: string;
  status: string;
}

type BankLogoMap = Record<string, string>;
type FailedLogoMap = Record<string, true>;

interface OpenBankingFetchResponse {
  success: boolean;
  bankLink?: string;
  providerLogoUri?: string;
  providerDisplayName?: string;
  status?: string;
  successful?: boolean;
  inProgress?: boolean;
}

interface TransactionTotals {
  totalTransactions: number;
  incomeTransCount: number;
  expenseTransCount: number;
  totalIncome: number;
  totalExpenses: number;
}

interface LmxAccountResponse {
  id?: number;
  customerId?: number;
  accountId?: string;
  sessionId?: string;
  accountNumber?: string;
  institutionName?: string;
  lmxApiSearchDetailsId?: number;
  accounts?: Array<{
    accountId?: string;
    sessionId?: string;
  }>;
}

interface ConnectedAccount {
  accountId: string | null;
  accountNumber: string | null;
  institutionName: string | null;
  lmxApiSearchDetailsId: number | null;
}

const ObSummaryPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const searchId =
    searchParams.get('SearchID') || searchParams.get('searchID') || searchParams.get('searchid');
  const { openBankingOnly, user } = useAuth();

  const [bankData, setBankData] = useState<BankData | null>(null);
  const [bankLogoMap, setBankLogoMap] = useState<BankLogoMap>({});
  const [failedLogos, setFailedLogos] = useState<FailedLogoMap>({});
  const [connectedAccounts, setConnectedAccounts] = useState<ConnectedAccount[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [totals, setTotals] = useState<TransactionTotals | null>(null);
  const [totalsLoading, setTotalsLoading] = useState<boolean>(false);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [confirming, setConfirming] = useState<boolean>(false);
  const [startingOpenBanking, setStartingOpenBanking] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2
    }).format(value);

  const formatDateDmy = (date: Date): string => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear());
    return `${day}/${month}/${year}`;
  };

  const getLast31DaysRange = (): string => {
    const today = new Date();
    const thirtyOneDaysAgo = new Date(today);
    thirtyOneDaysAgo.setDate(today.getDate() - 30);
    const startDate = formatDateDmy(thirtyOneDaysAgo);
    const endDate = formatDateDmy(today);
    return `${startDate} - ${endDate}`;
  };

  const last31DaysRange = useMemo(() => getLast31DaysRange(), []);

  const isRecord = (val: unknown): val is Record<string, unknown> =>
    typeof val === 'object' && val !== null && !Array.isArray(val);

  const normalizeBankName = (name: string): string => name.trim().toLowerCase().replace(/\s+/g, ' ');
  const normalizeBankNameLoose = (name: string): string =>
    normalizeBankName(name).replace(/\bbank\b/g, '').replace(/\s+/g, ' ').trim();

  const getApiErrorMessage = (err: unknown): string | null => {
    if (!isRecord(err)) return null;
    const response = err.response;
    if (!isRecord(response)) return null;
    const data = response.data;
    if (typeof data === 'string') return data;
    if (!isRecord(data)) return null;
    const message = data.message;
    return typeof message === 'string' ? message : null;
  };

  const selectedAccount = useMemo(() => {
    if (!selectedAccountId) return null;
    return connectedAccounts.find((a) => a.accountId === selectedAccountId) ?? null;
  }, [connectedAccounts, selectedAccountId]);

  const selectedInstitutionLabel =
    selectedAccount?.institutionName ?? bankData?.providerDisplayName ?? null;

  const selectedAccountIdRef = useRef<string | null>(null);
  useEffect(() => {
    selectedAccountIdRef.current = selectedAccountId;
  }, [selectedAccountId]);

  // Load bank display info from localStorage (set on confirm page when OB completes).
  // If not found (e.g., page refresh / storage cleared), fall back to fetching it from the API.
  useEffect(() => {
    if (!searchId) {
      return;
    }

    const bankKey = `ob_bank_${searchId}`;
    let cancelled = false;

    try {
      const stored = window.localStorage.getItem(bankKey);
      if (stored) {
        const parsedUnknown: unknown = JSON.parse(stored);
        const providerLogoUri =
          isRecord(parsedUnknown) && typeof parsedUnknown.providerLogoUri === 'string'
            ? parsedUnknown.providerLogoUri
            : '';
        const providerDisplayName =
          isRecord(parsedUnknown) && typeof parsedUnknown.providerDisplayName === 'string'
            ? parsedUnknown.providerDisplayName
            : 'Your Bank';
        const status =
          isRecord(parsedUnknown) && typeof parsedUnknown.status === 'string'
            ? parsedUnknown.status
            : 'Complete';

        setBankData({ providerLogoUri, providerDisplayName, status });
        return () => {
          cancelled = true;
        };
      }
    } catch (err: unknown) {
      // eslint-disable-next-line no-console
      console.error('Failed to read stored Open Banking bank data', err);
    }

    const fetchBankInfo = async () => {
      try {
        const customerId = await getCustomerId();
        if (!customerId) return;

        const response = await httpClient.post<OpenBankingFetchResponse>(
          '/iehub-customer/api/v1/openbanking/fetch',
          buildOpenBankingFetchPayload(Number(customerId), searchId ?? '', user?.isPartner)
        );

        if (cancelled) return;

        const data = response.data;
        const providerLogoUri = typeof data.providerLogoUri === 'string' ? data.providerLogoUri : '';
        const providerDisplayName =
          typeof data.providerDisplayName === 'string' ? data.providerDisplayName : 'Your Bank';
        const status = typeof data.status === 'string' ? data.status : 'Complete';

        setBankData({ providerLogoUri, providerDisplayName, status });
      } catch (err: unknown) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch Open Banking bank data', err);
      }
    };

    void fetchBankInfo();

    return () => {
      cancelled = true;
    };
  }, [searchId, user?.isPartner]);

  // Build a mapping of bank name -> logo URI from any stored OB sessions.
  // This allows us to show logos for previously linked banks (e.g. Wag) as well.
  useEffect(() => {
    const map: BankLogoMap = {};
    try {
      const storage = window.localStorage;
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (!key || !key.startsWith('ob_bank_')) continue;
        const raw = storage.getItem(key);
        if (!raw) continue;
        const parsed: unknown = JSON.parse(raw);
        if (!isRecord(parsed)) continue;
        const providerDisplayName =
          typeof parsed.providerDisplayName === 'string' ? parsed.providerDisplayName : '';
        const providerLogoUri = typeof parsed.providerLogoUri === 'string' ? parsed.providerLogoUri : '';
        if (!providerDisplayName || !providerLogoUri) continue;
        map[normalizeBankName(providerDisplayName)] = providerLogoUri;
        map[normalizeBankNameLoose(providerDisplayName)] = providerLogoUri;
      }
    } catch (err: unknown) {
      // eslint-disable-next-line no-console
      console.error('Failed to build bank logo map from localStorage', err);
    }

    // Prefer the current session's bank logo if present
    if (bankData?.providerDisplayName && bankData.providerLogoUri) {
      map[normalizeBankName(bankData.providerDisplayName)] = bankData.providerLogoUri;
      map[normalizeBankNameLoose(bankData.providerDisplayName)] = bankData.providerLogoUri;
    }

    setBankLogoMap(map);
  }, [bankData?.providerDisplayName, bankData?.providerLogoUri]);

  // Fetch connected accounts (used for selection + per-account totals)
  useEffect(() => {
    let cancelled = false;

    const fetchAccounts = async () => {
      try {
        const customerId = await getCustomerId();
        if (!customerId) {
          throw new Error('Unable to identify customer');
        }

        const accountsResp = await httpClient.get<LmxAccountResponse | LmxAccountResponse[]>(
          '/iehub-customer/api/v1/ie-lmx-accounts',
          {
            params: { customerId: Number(customerId) }
          }
        );

        const list = (Array.isArray(accountsResp.data) ? accountsResp.data : [accountsResp.data]).map(
          (item): ConnectedAccount => ({
            accountId:
              typeof item?.accountId === 'string' && item.accountId.trim().length > 0
                ? item.accountId
                : null,
            accountNumber:
              typeof item?.accountNumber === 'string' && item.accountNumber.trim().length > 0
                ? item.accountNumber
                : null,
            institutionName:
              typeof item?.institutionName === 'string' && item.institutionName.trim().length > 0
                ? item.institutionName
                : null,
            lmxApiSearchDetailsId:
              typeof item?.lmxApiSearchDetailsId === 'number' ? item.lmxApiSearchDetailsId : null
          })
        );

        if (cancelled) return;
        setConnectedAccounts(list);

        const currentSelected = selectedAccountIdRef.current;
        const selectedStillExists =
          !!currentSelected && list.some((a) => a.accountId === currentSelected);

        if (selectedStillExists) return;

        const preferredByInstitution =
          bankData?.providerDisplayName && list.length > 0
            ? list.find((a) => a.institutionName === bankData.providerDisplayName)?.accountId ?? null
            : null;

        const firstAccountId = list[0]?.accountId ?? null;
        const accountToSelect = preferredByInstitution || firstAccountId;
        if (accountToSelect) {
          setSelectedAccountId(accountToSelect);
        }
      } catch (err: unknown) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error('Failed to load Open Banking connected accounts', err);
        const parsed = parseUnknownError(err);
        setError(parsed.message || 'Failed to load connected accounts. Please try again.');
        setInitialLoading(false);
      }
    };

    void fetchAccounts();

    return () => {
      cancelled = true;
    };
  }, [bankData?.providerDisplayName]);

  // Fetch totals for the selected account
  useEffect(() => {
    let cancelled = false;

    const fetchTotalsForSelected = async () => {
      if (!selectedAccountId) {
        setTotals(null);
        setInitialLoading(false);
        return;
      }

      setTotalsLoading(true);
      try {
        const customerId = await getCustomerId();
        if (!customerId) {
          throw new Error('Unable to identify customer');
        }

        const totalsResp = await httpClient.get<
          | {
              customerId: number;
              incomeTransCount?: number;
              expenseTransCount?: number;
              totalIncome?: number;
              totalExpenses?: number;
            }
          | Array<{
              customerId: number;
              incomeTransCount?: number;
              expenseTransCount?: number;
              totalIncome?: number;
              totalExpenses?: number;
            }>
        >('/iehub-customer/api/v1/lmx-acc-trans-totals-v', {
          params: {
            customerId: Number(customerId),
            accountId: selectedAccountId
          }
        });

        const totalsPayload = Array.isArray(totalsResp.data) ? totalsResp.data[0] : totalsResp.data;

        const incomeTransCount = totalsPayload?.incomeTransCount ?? 0;
        const expenseTransCount = totalsPayload?.expenseTransCount ?? 0;
        const totalIncome = totalsPayload?.totalIncome ?? 0;
        const totalExpenses = totalsPayload?.totalExpenses ?? 0;

        if (cancelled) return;
        setTotals({
          totalTransactions: incomeTransCount + expenseTransCount,
          incomeTransCount,
          expenseTransCount,
          totalIncome,
          totalExpenses
        });
      } catch (err: unknown) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error('Failed to load Open Banking transaction totals', err);
        const parsed = parseUnknownError(err);
        setError(parsed.message || 'Failed to load transaction totals. Please try again.');
      } finally {
        if (cancelled) return;
        setTotalsLoading(false);
        setInitialLoading(false);
      }
    };

    void fetchTotalsForSelected();

    return () => {
      cancelled = true;
    };
  }, [selectedAccountId]);

  const handleBackToDashboard = () => {
    navigate(openBankingOnly ? '/open-banking' : '/dashboard');
  };

  const handleAddAnotherBank = async () => {
    setStartingOpenBanking(true);
    setError(null);
    try {
      const customerId = await getCustomerId();
      if (!customerId) {
        throw new Error('Unable to identify customer');
      }

      const response = await httpClient.post<OpenBankingFetchResponse>(
        '/iehub-customer/api/v1/openbanking/fetch',
        buildOpenBankingFetchPayload(Number(customerId), '', user?.isPartner)
      );

      const data = response.data;
      const bankLink = typeof data.bankLink === 'string' ? data.bankLink : '';
      if (data.success && bankLink.trim().length > 0) {
        window.location.href = bankLink;
        return;
      }

      setError('Failed to start Open Banking. Please try again.');
    } catch (err: unknown) {
      // eslint-disable-next-line no-console
      console.error('Failed to start Open Banking flow from summary', err);
      const apiMessage = getApiErrorMessage(err);
      const parsed = parseUnknownError(err);
      setError(apiMessage ?? parsed.message ?? 'Failed to start Open Banking. Please try again.');
    } finally {
      setStartingOpenBanking(false);
    }
  };

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      const customerId = await getCustomerId();

      if (!customerId) {
        throw new Error('Unable to identify customer');
      }

      // Legacy parity:
      // - Creditor/agent-invited ("Open Banking-only") users are sharing transactions with the inviting company
      //   and should NOT import them into their own budget (skip LMX update-to-main).
      // - Normal customers should import transactions into the main budget (call LMX update-to-main).
      if (!openBankingOnly) {
        await httpClient.post('/iehub-customer/api/v1/lmx-data-update-to-main', {
          customerId: Number(customerId)
        });
      }

      // Legacy parity: if the user arrived via creditor Open Banking invitation,
      // update invitation status after the customer "Submit"s the OB flow.
      const isPartnerUser = String(user?.isPartner ?? 'N').trim().toUpperCase() === 'Y';
      // Team rule: only agent-invited Open Banking journeys should touch ie-openbank-invitation.
      if (openBankingOnly && typeof user?.userId === 'number' && user.userId > 0) {
        try {
          const invites = await OnboardingService.getOpenBankInvitationsByUserId(user.userId, {
            skipErrorCapture: true,
          });
          const first = Array.isArray(invites) ? invites[0] : undefined;
          if (first && typeof first.id === 'number') {
            const now = Date.now();
            const updates = isPartnerUser
              ? {
                  // Partner path: update partner status fields
                  partnersStatus: 'Submitted',
                  partnersStatusDate: now,
                }
              : {
                  // Customer path: update customer status fields
                  status: 'Submitted',
                  customerStatusDate: now,
                };
            await OnboardingService.updateOpenBankInvitation(
              first,
              updates,
              { skipErrorCapture: true }
            );
          }
        } catch (inviteError) {
          // Non-blocking: do not prevent the customer from completing the journey.
          // eslint-disable-next-line no-console
          console.warn('Failed to update Open Banking invitation status', inviteError);
        }
      }

      navigate(openBankingOnly ? '/ob-thank-you' : '/update-budget');
    } catch (err: unknown) {
      // eslint-disable-next-line no-console
      console.error('Failed to confirm Open Banking transactions', err);
      const apiMessage = getApiErrorMessage(err);
      const parsed = parseUnknownError(err);
      setError(apiMessage ?? parsed.message ?? 'Failed to confirm transactions. Please try again.');
    } finally {
      setConfirming(false);
    }
  };

  const StatCard: React.FC<{
    label: string;
    value: number;
    variant: 'neutral' | 'success' | 'warning';
  }> = ({ label, value, variant }) => {
    const variantStyles = {
      neutral: {
        bg: '#f8fafc',
        border: '#e2e8f0',
        valueColor: '#1e293b',
        labelColor: '#64748b'
      },
      success: {
        bg: '#f0fdf4',
        border: '#bbf7d0',
        valueColor: '#166534',
        labelColor: '#15803d'
      },
      warning: {
        bg: '#fefce8',
        border: '#fef08a',
        valueColor: '#854d0e',
        labelColor: '#a16207'
      }
    } as const;

    const styles = variantStyles[variant];

    return (
      <Box data-testid="pages-private-from-dashboard-stat-card-container-action-2"
        sx={{
          backgroundColor: styles.bg,
          border: `1px solid ${styles.border}`,
          borderRadius: '12px',
          p: 2,
          textAlign: 'center'
        }}
      >
        <Typography data-testid="pages-private-from-dashboard-stat-card-text-action-1-2"
          sx={{
            color: styles.valueColor,
            fontWeight: 700,
            fontSize: '28px',
            lineHeight: 1.2,
            mb: 0.5
          }}
        >
          {value.toLocaleString()}
        </Typography>
        <Typography data-testid="pages-private-from-dashboard-stat-card-text-action-2-2"
          sx={{
            color: styles.labelColor,
            fontWeight: 500,
            fontSize: '13px'
          }}
        >
          {label}
        </Typography>
      </Box>
    );
  };

  const SummaryView = () => (
    <Box data-testid="pages-private-from-dashboard-summary-view-container-action-1"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        width: '100%',
        maxWidth: '900px',
        mx: 'auto'
      }}
    >
      {/* Page Header */}
      <Box data-testid="pages-private-from-dashboard-summary-view-container-action-2" sx={{ mb: 1 }}>
        <Typography data-testid="pages-private-from-dashboard-summary-view-text-confirm-your-transactions"
          variant="h4"
          sx={{
            fontWeight: 700,
            fontSize: { xs: '22px', md: '26px' },
            color: '#00474d',
            mb: 1
          }}
        >
          Confirm Your Transactions
        </Typography>
        <Typography data-testid="pages-private-from-dashboard-summary-view-text-action-1"
          variant="body2"
          sx={{
            fontSize: '15px',
            color: '#64748b',
            lineHeight: 1.6
          }}
        >
          {openBankingOnly
            ? 'Review the summary below and confirm to finish linking your bank.'
            : 'Review the summary below and confirm to proceed with your budget analysis.'}
        </Typography>
        {error && (
          <Typography data-testid="pages-private-from-dashboard-summary-view-text-action-2"
            variant="body2"
            sx={{
              mt: 2,
              color: '#b91c1c',
              fontSize: '14px'
            }}
          >
            {error}
          </Typography>
        )}
      </Box>

      {/* Cards Container */}
      <Box data-testid="pages-private-from-dashboard-summary-view-container-action-3"
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '320px 1fr' },
          gap: 3
        }}
      >
        {/* Connected Bank Card */}
        <Paper data-testid="pages-private-from-dashboard-summary-view-container-action-4"
          elevation={0}
          sx={{
            p: 3,
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            backgroundColor: '#fff',
            height: 'fit-content'
          }}
        >
          <Typography data-testid="pages-private-from-dashboard-summary-view-text-connected-accounts"
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: '16px',
              color: '#1e293b',
              mb: 2.5
            }}
          >
            Connected Accounts
          </Typography>

          {/* Bank Connection Status */}
          <Box data-testid="pages-private-from-dashboard-summary-view-container-action-5" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
            {(connectedAccounts.length > 0 ? connectedAccounts : [
              {
                accountId: null,
                accountNumber: null,
                institutionName: bankData?.providerDisplayName ?? 'Your Bank',
                lmxApiSearchDetailsId: null
              }
            ]).map((acc, idx) => {
              const institutionLabel = acc.institutionName || bankData?.providerDisplayName || 'Your Bank';
              const normalizedName = normalizeBankName(institutionLabel);
              const looseName = normalizeBankNameLoose(institutionLabel);
              const resolvedKey =
                bankLogoMap[normalizedName] && bankLogoMap[normalizedName].length > 0
                  ? normalizedName
                  : bankLogoMap[looseName] && bankLogoMap[looseName].length > 0
                    ? looseName
                    : null;
              const logoUri = resolvedKey ? bankLogoMap[resolvedKey] : '';
              const hasLogo = !!logoUri && !!resolvedKey && !failedLogos[resolvedKey];
              const isSelected = !!acc.accountId && acc.accountId === selectedAccountId;

              return (
                <ListItemButton data-testid="pages-private-from-dashboard-summary-view-btn-action"
                  key={`${acc.accountId ?? 'acc'}-${acc.lmxApiSearchDetailsId ?? 'sid'}-${idx}`}
                  onClick={() => {
                    if (acc.accountId) {
                      setSelectedAccountId(acc.accountId);
                    }
                  }}
                  disabled={!acc.accountId}
                  sx={{
                    backgroundColor: isSelected ? '#ecfdf5' : '#f0fdf4',
                    border: isSelected ? '2px solid #16a34a' : '1px solid #bbf7d0',
                    borderRadius: '12px',
                    p: 2,
                    '&.Mui-disabled': {
                      opacity: 0.7
                    }
                  }}
                >
                  <Box data-testid="pages-private-from-dashboard-summary-view-container-action-6" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box data-testid="pages-private-from-dashboard-summary-view-container-action-7"
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        backgroundColor: '#dcfce7',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}
                    >
                      {hasLogo ? (
                        <Box data-testid="pages-private-from-dashboard-summary-view-container-action-8"
                          component="img"
                          src={logoUri}
                          alt={institutionLabel || 'Bank'}
                          onError={() => {
                            if (!resolvedKey) return;
                            setFailedLogos((prev) => ({ ...prev, [resolvedKey]: true }));
                          }}
                          sx={{
                            width: 26,
                            height: 26,
                            objectFit: 'contain'
                          }}
                        />
                      ) : (
                        <CheckCircleIcon sx={{ fontSize: 26, color: '#16a34a' }} />
                      )}
                    </Box>
                    <Box data-testid="pages-private-from-dashboard-summary-view-container-action-9" sx={{ flex: 1, minWidth: 0 }}>
                      <Typography data-testid="pages-private-from-dashboard-summary-view-text-action-3"
                        sx={{
                          fontWeight: 700,
                          fontSize: '15px',
                          color: '#166534',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {institutionLabel}
                      </Typography>
                      <Typography data-testid="pages-private-from-dashboard-summary-view-text-action-4"
                        variant="caption"
                        sx={{
                          color: '#15803d',
                          fontSize: '13px'
                        }}
                      >
                        {acc.accountNumber ? `Account number: ${acc.accountNumber}` : 'Connected successfully'}
                      </Typography>
                    </Box>
                  </Box>
                </ListItemButton>
              );
            })}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Add Another Bank Button */}
          <Button data-testid="pages-private-from-dashboard-summary-view-btn-handle-add-another-bank"
            variant="outlined"
            onClick={handleAddAnotherBank}
            startIcon={<AddIcon />}
            fullWidth
            disabled={startingOpenBanking}
            sx={{
              textTransform: 'none',
              borderRadius: '10px',
              borderStyle: 'dashed',
              borderColor: '#94a3b8',
              borderWidth: '1.5px',
              color: '#475569',
              fontWeight: 600,
              fontSize: '14px',
              py: 1.25,
              '&:hover': {
                borderColor: '#00474d',
                backgroundColor: '#f0fdfa',
                color: '#00474d'
              }
            }}
          >
            {startingOpenBanking ? (
              <Box data-testid="pages-private-from-dashboard-summary-view-container-action-10" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={18} sx={{ color: '#475569' }} />
                <span>Opening...</span>
              </Box>
            ) : (
              'Add Another Bank'
            )}
          </Button>
        </Paper>

        {/* Transaction Summary Card */}
        <Paper data-testid="pages-private-from-dashboard-summary-view-container-action-11"
          elevation={0}
          sx={{
            p: 3,
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            backgroundColor: '#fff'
          }}
        >
          <Typography data-testid="pages-private-from-dashboard-summary-view-text-transaction-summary"
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: '16px',
              color: '#1e293b',
              mb: 2.5
            }}
          >
            Transaction Summary
          </Typography>
          <Typography data-testid="pages-private-from-dashboard-summary-view-text-action-5"
            variant="caption"
            sx={{
              display: 'block',
              color: '#64748b',
              fontSize: '13px',
              mb: 2.5,
              mt: -1.75,
            }}
          >
            {last31DaysRange}
          </Typography>
          {selectedInstitutionLabel && (
            <Typography data-testid="pages-private-from-dashboard-summary-view-text-action-6"
              variant="caption"
              sx={{
                display: 'block',
                color: '#475569',
                fontSize: '13px',
                mb: 2.5,
                mt: -2,
              }}
            >
              {selectedAccount?.accountNumber
                ? `Showing summary for: ${selectedInstitutionLabel} • ${selectedAccount.accountNumber}`
                : `Showing summary for: ${selectedInstitutionLabel}`}
            </Typography>
          )}

          {totalsLoading ? (
            <Box data-testid="pages-private-from-dashboard-summary-view-container-action-12"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 6,
                gap: 2
              }}
            >
              <CircularProgress size={32} sx={{ color: '#00474d' }} />
              <Typography data-testid="pages-private-from-dashboard-summary-view-text-loading-transaction-summary..." variant="body2" sx={{ color: '#64748b' }}>
                Loading transaction summary...
              </Typography>
            </Box>
          ) : (
            <>
              {/* Stats Grid */}
              <Box data-testid="pages-private-from-dashboard-summary-view-container-action-13"
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                  gap: 2,
                  mb: 3
                }}
              >
                <StatCard
                  label="Total Transactions"
                  value={totals?.totalTransactions ?? 0}
                  variant="neutral"
                />
                <StatCard
                  label="Credit Transactions"
                  value={totals?.incomeTransCount ?? 0}
                  variant="success"
                />
                <StatCard
                  label="Debit Transactions"
                  value={totals?.expenseTransCount ?? 0}
                  variant="warning"
                />
              </Box>

              {/* Totals Section */}
              <Box data-testid="pages-private-from-dashboard-summary-view-container-action-14" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box data-testid="pages-private-from-dashboard-summary-view-container-action-15"
                  sx={{
                    background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                    borderRadius: '12px',
                    p: 2.5,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    border: '1px solid #a7f3d0'
                  }}
                >
                  <Box data-testid="pages-private-from-dashboard-summary-view-container-action-16">
                    <Typography data-testid="pages-private-from-dashboard-summary-view-text-total-credits"
                      sx={{
                        color: '#166534',
                        fontWeight: 600,
                        fontSize: '14px',
                        mb: 0.5
                      }}
                    >
                      Total Credits
                    </Typography>
                    <Typography data-testid="pages-private-from-dashboard-summary-view-text-money-received"
                      sx={{
                        color: '#15803d',
                        fontSize: '12px'
                      }}
                    >
                      Money received
                    </Typography>
                  </Box>
                  <Typography data-testid="pages-private-from-dashboard-summary-view-text-action-7"
                    sx={{
                      color: '#166534',
                      fontWeight: 700,
                      fontSize: '20px'
                    }}
                  >
                    {formatCurrency(totals?.totalIncome ?? 0)}
                  </Typography>
                </Box>

                <Box data-testid="pages-private-from-dashboard-summary-view-container-action-17"
                  sx={{
                    background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
                    borderRadius: '12px',
                    p: 2.5,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    border: '1px solid #fecaca'
                  }}
                >
                  <Box data-testid="pages-private-from-dashboard-summary-view-container-action-18">
                    <Typography data-testid="pages-private-from-dashboard-summary-view-text-total-debits"
                      sx={{
                        color: '#991b1b',
                        fontWeight: 600,
                        fontSize: '14px',
                        mb: 0.5
                      }}
                    >
                      Total Debits
                    </Typography>
                    <Typography data-testid="pages-private-from-dashboard-summary-view-text-money-spent"
                      sx={{
                        color: '#b91c1c',
                        fontSize: '12px'
                      }}
                    >
                      Money spent
                    </Typography>
                  </Box>
                  <Typography data-testid="pages-private-from-dashboard-summary-view-text-action-8"
                    sx={{
                      color: '#991b1b',
                      fontWeight: 700,
                      fontSize: '20px'
                    }}
                  >
                    {formatCurrency(totals ? Math.abs(totals.totalExpenses) : 0)}
                  </Typography>
                </Box>
              </Box>

              {/* Action Buttons */}
              <Box data-testid="pages-private-from-dashboard-summary-view-container-action-19"
                sx={{
                  mt: 4,
                  pt: 3,
                  borderTop: '1px solid #e2e8f0',
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'flex-end',
                  gap: 2
                }}
              >
                <Button data-testid="pages-private-from-dashboard-summary-view-btn-handle-back-to-dashboard"
                  variant="outlined"
                  onClick={handleBackToDashboard}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '10px',
                    borderColor: '#e2e8f0',
                    color: '#64748b',
                    fontWeight: 600,
                    px: 3,
                    py: 1.25,
                    order: { xs: 2, sm: 1 },
                    '&:hover': {
                      borderColor: '#cbd5e1',
                      backgroundColor: '#f8fafc'
                    }
                  }}
                >
                  {openBankingOnly ? 'Cancel' : 'Cancel'}
                </Button>
                <Button data-testid="pages-private-from-dashboard-summary-view-btn-handle-confirm"
                  variant="contained"
                  onClick={handleConfirm}
                  disabled={confirming || totalsLoading}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '10px',
                    backgroundColor: '#d4e03b',
                    color: '#003d3b',
                    fontWeight: 700,
                    px: 4,
                    py: 1.25,
                    fontSize: '15px',
                    order: { xs: 1, sm: 2 },
                    boxShadow: '0 2px 8px rgba(212, 224, 59, 0.4)',
                    '&:hover': {
                      backgroundColor: '#c5d630',
                      boxShadow: '0 4px 12px rgba(212, 224, 59, 0.5)'
                    },
                    '&:disabled': {
                      backgroundColor: '#e2e8f0',
                      color: '#94a3b8'
                    }
                  }}
                >
                  {confirming ? (
                    <Box data-testid="pages-private-from-dashboard-summary-view-container-action-20" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={18} sx={{ color: '#003d3b' }} />
                      <span>Confirming...</span>
                    </Box>
                  ) : (
                    openBankingOnly ? 'Submit' : 'Confirm & Continue'
                  )}
                </Button>
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );

  const PageLayout = () => (
    <Box data-testid="pages-private-from-dashboard-page-layout-container-action-1-2"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: '#f8fafc'
      }}
    >
      <Header data-testid="pages-private-from-dashboard-page-layout-header-action-2" />

      <Box data-testid="pages-private-from-dashboard-page-layout-container-action-2-2"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          justifyContent: 'flex-start',
          overflow: 'auto',
          px: { xs: 2, md: 4 },
          py: { xs: 3, md: 4 },
          pb: { xs: 12, md: 4 }
        }}
      >
        {initialLoading ? (
          <Box data-testid="pages-private-from-dashboard-page-layout-container-action-3-2"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '60vh',
              gap: 3
            }}
          >
            <CircularProgress size={48} sx={{ color: '#00474d' }} />
            <Typography data-testid="pages-private-from-dashboard-page-layout-text-loading-transaction-summary"
              variant="h6"
              sx={{
                color: '#1e293b',
                fontWeight: 600,
                fontSize: '18px'
              }}
            >
              Loading Transaction Summary
            </Typography>
            <Typography data-testid="pages-private-from-dashboard-page-layout-text-action-1"
              variant="body2"
              sx={{
                color: '#64748b',
                fontSize: '14px',
                textAlign: 'center',
                maxWidth: '400px'
              }}
            >
              Please wait while we fetch your transaction data from your connected bank account...
            </Typography>
            {error && (
              <Typography data-testid="pages-private-from-dashboard-page-layout-text-action-2"
                variant="body2"
                sx={{
                  mt: 2,
                  color: '#b91c1c',
                  fontSize: '14px',
                  textAlign: 'center',
                  maxWidth: '400px'
                }}
              >
                {error}
              </Typography>
            )}
          </Box>
        ) : (
          <SummaryView />
        )}
      </Box>

      {/* Mobile Bottom Nav */}
      <Box data-testid="pages-private-from-dashboard-page-layout-container-action-4" sx={{ display: { xs: 'block', md: 'none' } }}>
        <BottomTabBar data-testid="pages-private-from-dashboard-page-layout-nav-bar-action-2" />
      </Box>
    </Box>
  );

  return <PageLayout />;
};

export default ObSummaryPage;
